import type { FormField } from '../models/form';
import { toEnglishNumbers } from './numbers';

export type SimpleCaptchaMode = 'digits' | 'math' | 'mixed' | 'letters';
export type SimpleCaptchaDigitStyle = 'persian' | 'latin';
export type SimpleCaptchaVisualStyle = 'easy' | 'normal';

interface CaptchaRegistryEntry {
  challengeId: string;
  answer: string;
  verifiedAnswer?: string;
  verifiedAt?: number;
}

const registry = new Map<string, CaptchaRegistryEntry>();
const refreshListeners = new Map<string, Set<() => void>>();
const VERIFIED_CACHE_MS = 4 * 60 * 1000;

export function resolveSimpleCaptchaMode(field: FormField): SimpleCaptchaMode {
  if (field.simpleCaptchaMode) return field.simpleCaptchaMode;
  return field.captchaContainsLetters ? 'mixed' : 'digits';
}

export function resolveSimpleCaptchaLength(field: FormField): number {
  const raw = Number(field.captchaLength ?? 4);
  if (!Number.isFinite(raw)) return 4;
  return Math.max(3, Math.min(8, Math.round(raw)));
}

export function resolveSimpleCaptchaDigitStyle(field: FormField): SimpleCaptchaDigitStyle {
  return field.simpleCaptchaDigitStyle === 'latin' ? 'latin' : 'persian';
}

export function resolveSimpleCaptchaVisualStyle(field: FormField): SimpleCaptchaVisualStyle {
  return field.simpleCaptchaVisualStyle === 'normal' ? 'normal' : 'easy';
}

export function normalizeSimpleCaptchaInput(value: string, mode: SimpleCaptchaMode): string {
  const english = toEnglishNumbers(value).replace(/[\s\u200c\u200d\u2060]/g, '').toUpperCase();
  if (mode === 'digits' || mode === 'math') return english.replace(/[^0-9]/g, '');
  if (mode === 'letters') return english.replace(/[^A-Z]/g, '');
  return english.replace(/[^A-Z0-9]/g, '');
}

export function createSimpleCaptchaChallengeId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID().replace(/-/g, '');
  }
  const random = Math.random().toString(36).slice(2);
  return `${Date.now().toString(36)}${random}`.replace(/[^a-z0-9]/gi, '').slice(0, 40);
}

export function registerSimpleCaptchaChallenge(fieldId: string, challengeId: string): void {
  const current = registry.get(fieldId);
  registry.set(fieldId, {
    challengeId,
    answer: current?.challengeId === challengeId ? current.answer : ''
  });
}

export function updateSimpleCaptchaAnswer(fieldId: string, challengeId: string, answer: string): void {
  const current = registry.get(fieldId);
  registry.set(fieldId, {
    challengeId,
    answer,
    verifiedAnswer: current?.challengeId === challengeId && current.verifiedAnswer === answer ? current.verifiedAnswer : undefined,
    verifiedAt: current?.challengeId === challengeId && current.verifiedAnswer === answer ? current.verifiedAt : undefined
  });
}

export function getSimpleCaptchaChallenge(fieldId: string, answer: string): { challengeId?: string; verified: boolean } {
  const current = registry.get(fieldId);
  if (!current || current.answer !== answer) return { challengeId: current?.challengeId, verified: false };
  const verified = current.verifiedAnswer === answer
    && typeof current.verifiedAt === 'number'
    && Date.now() - current.verifiedAt <= VERIFIED_CACHE_MS;
  return { challengeId: current.challengeId, verified };
}

export function markSimpleCaptchaVerified(fieldId: string, challengeId: string, answer: string): void {
  const current = registry.get(fieldId);
  if (!current || current.challengeId !== challengeId || current.answer !== answer) return;
  registry.set(fieldId, { ...current, verifiedAnswer: answer, verifiedAt: Date.now() });
}

export function clearSimpleCaptchaChallenge(fieldId: string, challengeId?: string): void {
  const current = registry.get(fieldId);
  if (!current || (challengeId && current.challengeId !== challengeId)) return;
  registry.delete(fieldId);
}

export function subscribeSimpleCaptchaRefresh(fieldId: string, listener: () => void): () => void {
  const listeners = refreshListeners.get(fieldId) ?? new Set<() => void>();
  listeners.add(listener);
  refreshListeners.set(fieldId, listeners);
  return () => {
    const current = refreshListeners.get(fieldId);
    current?.delete(listener);
    if (!current?.size) refreshListeners.delete(fieldId);
  };
}

export function requestSimpleCaptchaRefresh(fieldId: string): void {
  for (const listener of refreshListeners.get(fieldId) ?? []) listener();
}
